<template>
  <div class="chat-page" ref="chatRef" @scroll="handleScroll">
    <div class="page-surface">
      <div class="list-shell">
        <div v-if="initialLoading" class="skeleton-group">
          <div class="skeleton-card task-card" v-for="n in 3" :key="n">
            <div class="item-header">
              <div class="skeleton-dot"></div>
              <div class="skeleton-line header-line"></div>
            </div>
            <div class="skeleton-body">
              <div class="skeleton-left">
                <div class="skeleton-thumb"></div>
                <div class="skeleton-button"></div>
              </div>
              <div class="skeleton-preview">
                <div class="skeleton-rect"></div>
                <div class="skeleton-actions">
                  <div class="skeleton-action"></div>
                  <div class="skeleton-action"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <template v-else>
          <div
              v-if="tasks.length"
              class="task-list"
              :class="{ 'column-reverse': true }"
          >
            <article
                v-for="task in tasks"
                :key="task.id"
                class="task-card"
            >
              <div class="item-body">
                <div class="item-left">
                  <div class="source-thumb">
                    <img
                        :src="task.sourceImage || placeholderImage"
                        alt="source preview"
                    />
                  </div>
                  <button
                      class="ghost-btn"
                      :disabled="actionLoading === task.id || isGenerating(task)"
                      @click="regenerateTask(task)"
                  >
                    <span class="ghost-icon refresh"></span>
                    <span>{{ actionLoading === task.id ? '处理中...' : '重新生成' }}</span>
                  </button>
                </div>
                <div
                    class="item-preview"
                    :class="{ loading: isGenerating(task) }"
                    @mouseenter="hoverId = task.id"
                    @mouseleave="hoverId = ''"
                    @dblclick.stop="toggleFullscreen(task.id)"
                >
                  <template v-if="!isGenerating(task) && task.videoUrl">
                    <video
                        :class="['preview-video', { 'fullscreen-video': fullscreenVideoId === task.id }]"
                        :src="task.videoUrl"
                        playsinline
                        :poster="task.sourceImage"
                        :ref="(el) => setVideoRef(task.id, el)"
                        @play="handlePlay(task.id)"
                        @pause="handlePause(task.id)"
                        @timeupdate="handleTimeUpdate(task.id, $event)"
                        @dblclick.stop="toggleFullscreen(task.id)"
                    />
                    <div
                        v-if="completionOverlays[task.id]"
                        class="completion-overlay"
                    >
                      <div class="loading-ring-wrapper">
                        <div class="loading-ring"></div>
                        <div class="loading-ring-center">
                          <span class="loading-ring-percent">
                            {{ displayProgressText(task) }}
                          </span>
                        </div>
                      </div>
                    </div>
                    <button
                        class="play-overlay"
                        :class="{ visible: hoverId === task.id }"
                        @click="toggleVideo(task.id)"
                        @dblclick.stop="toggleFullscreen(task.id)"
                        @mouseenter="hoverId = task.id"
                        @mouseleave="hoverId = ''"
                    >
                      <img
                          :src="isPlaying(task.id) ? pauseIcon : playIcon"
                          :alt="isPlaying(task.id) ? 'pause' : 'play'"
                          class="play-toggle-icon"
                      />
                    </button>
                    <div class="duration-chip">{{ displayDuration(task) }}</div>
                    <div class="action-stack">
                      <button
                          v-if="isCompleted(task) || isFailed(task)"
                          class="ghost-btn small filled"
                          @click="deleteTask(task)"
                      >
                        <span class="ghost-icon delete"></span>
                        <span>删除记录</span>
                      </button>
                      <button
                          v-if="isCompleted(task)"
                          class="ghost-btn small filled"
                          @click="downloadVideo(task)"
                      >
                        <span class="ghost-icon download"></span>
                        <span>下载视频</span>
                      </button>
                    </div>
                  </template>
                  <template v-else>
                    <div class="loading-card" :class="{ failed: isFailed(task) }">
                      <template v-if="isFailed(task)">
                        <div class="failed-thumb">
                          <img :src="failedImage" alt="生成失败"/>
                        </div>
                        <div class="failed-text">生成失败</div>
                        <div class="failed-tips-text">{{ getFailedText(task) }}</div>
                      </template>
                      <template v-else>
                        <div class="loading-ring-wrapper">
                          <div class="loading-ring"></div>
                          <div class="loading-ring-center">
                            <span class="loading-ring-percent">
                              {{ displayProgressText(task) }}
                            </span>
                          </div>
                        </div>
                        <div class="loading-stage-text">
                          <div class="loading-stage-main">{{ taskMainText(task) }}</div>
                          <div v-if="taskSubText(task)" class="loading-stage-sub">{{ taskSubText(task) }}</div>
                        </div>
                      </template>
                      <div class="loading-text">
                        <div>AI也可能犯错误，效果以实际为准</div>
                      </div>
                    </div>
                    <div class="action-stack loading-actions">
                      <button
                          v-if="isCompleted(task) || isFailed(task)"
                          class="ghost-btn small filled"
                          @click="deleteTask(task)"
                      >
                        <span class="ghost-icon delete"></span>
                        <span>删除记录</span>
                      </button>
                    </div>
                  </template>
                </div>
              </div>
            </article>
          </div>

          <div v-else class="empty-state">
            <div class="empty-title">暂无生成记录</div>
            <div class="empty-desc">上传一张图片，开始生成你的第一条视频</div>
          </div>

          <div class="load-tip" v-if="loadingMore">加载中...</div>
        </template>
      </div>
    </div>

    <aside
        class="floating-upload"
        :class="{ 'drag-over': isDragOver }"
        @dragover.prevent="handleDragOver"
        @dragleave.prevent="handleDragLeave"
        @drop.prevent="handleDrop"
    >
      <div class="upload-card">
        <button
            class="upload-cta"
            :class="{ loading: uploading }"
            :disabled="uploading"
            @click="handleUploadClick"
        >
          <img v-if="!uploading" :src="uploadIcon" alt="" class="cta-icon"/>
          <span>{{ uploading ? '玩命上传中...' : '继续上传' }}</span>
          <span v-if="uploading" class="cta-flash" aria-hidden="true"></span>
        </button>
        <div class="upload-copy">
          <div>建议上传图片能清晰展示商品，以保障生成效果</div>
          <div class="copy-sub">拖放或上传照片（JPG、PNG，最大4MB）</div>
        </div>
      </div>
      <input
          ref="fileInput"
          type="file"
          accept=".jpg,.jpeg,.png,.mp4"
          style="display: none"
          @change="handleFileChange"
      />
    </aside>

    <div v-if="showDeleteModal" class="delete-modal-mask">
      <div class="delete-modal">
        <div class="delete-title">确认删除该条记录？</div>
        <div class="delete-desc">删除记录后视频将不能恢复且下载，后续再生成需要扣除次数。确认删除记录？</div>
        <div class="delete-actions">
          <button
              class="modal-btn ghost"
              :disabled="deleteLoading"
              @click="closeDeleteModal"
          >
            再考虑一下
          </button>
          <button
              class="modal-btn danger"
              :disabled="deleteLoading"
              @click="confirmDelete"
          >
            {{ deleteLoading ? '删除中...' : '确认删除' }}
          </button>
        </div>
      </div>
    </div>
    <QuotaTipModal
        :visible="showQuotaModal"
        @close="showQuotaModal = false"
    />
  </div>
</template>

<script setup>
import {ref, onMounted, nextTick, inject, onUnmounted} from 'vue';
import {SUCCESS_CODE, listCvTasks, createCvTask, uploadClothesImage, getCvTaskStatus, deleteCvTask} from '@/api/api';
import Message from '@/utils/message';
import placeholderImage from '@/assets/clean_icon.png';
import playIcon from '@/assets/play_icon.png';
import pauseIcon from '@/assets/pause_icon.png';
import failedImage from '@/assets/cv_item_failed.png';
import QuotaTipModal from '@/components/QuotaTipModal.vue';
import { emitQuotaDelta } from '@/utils/quotaBus';

import uploadIcon from
    '@/assets/image_add_icon.png';

const chatRef = ref(null);
const tasks = ref([]);
const pageSize = 10;
const currentPage = ref(1);
const hasMore = ref(true);
const initialLoading = ref(true);
const loadingMore = ref(false);
const uploading = ref(false);
const fileInput = ref(null);
const actionLoading = ref('');
const hoverId = ref('');
const playingId = ref('');
const videoRefs = new Map();
const remainingTimes = ref({});
const fullscreenVideoId = ref('');
const pollTimer = ref(null);
const showDeleteModal = ref(false);
const pendingDeleteTask = ref(null);
const deleteLoading = ref(false);
const isDragOver = ref(false);
const showQuotaModal = ref(false);

const POLL_INTERVAL = 4000;

const showLoginModal = inject('showLoginModal');

const getCookie = (name) => {
  const matches = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return matches ? matches[2] : null;
};

const normalizeState = (state) => String(state ?? '');

const PROGRESS_STAGES = [
  {min: 0, max: 5, main: '正在识别商品信息', sub: '正在解析用户上传的图片,提取核心信息'},
  {min: 5, max: 10, main: '正在构思脚本大纲', sub: '规划视频的整体架构和叙事逻辑'},
  {min: 10, max: 13, main: '正在撰写口播文案', sub: '分析各平台最火视频文案，生成带货脚本,控制字数与口语化表达'},
  {min: 13, max: 15.5, main: '正在设计分镜', sub: '将文案转化为具体的视觉场景和镜头语言'},
  {min: 15.5, max: 27, main: '正在绘制画面', sub: '生成分镜首尾帧确认连贯一致性'},
  {min: 27, max: 55, main: '正在生成视频帧', sub: '开始生成动态片段进行一致性检验'},
  {min: 55, max: 64, main: '正在精心剪辑', sub: '将多个视频片段、转场效果进行组合拼接'},
  {min: 64, max: 72, main: '正在调配音效', sub: '为视频添加背景音乐和效果音,增强氛围'},
  {min: 72, max: 88, main: '正在合成配音', sub: '将文案合成为口语,并与视频画面进行对齐'},
  {min: 88, max: 96, main: '正在进行最终优化', sub: '统一色调、优化音频音量、后期处理'},
  {min: 96, max: 99.0001, main: '视频导出中', sub: '正在进行最后检验并编码压缩为最终格式，精彩即将呈现'},
];

const toNumber = (val) => {
  const num = Number(val);
  return Number.isFinite(num) ? num : null;
};

const findStageByProgress = (progress) => {
  const pct = toNumber(progress);
  if (pct === null) return null;
  return PROGRESS_STAGES.find((stage) => pct >= stage.min && pct < stage.max) || null;
};

const hasToken = () => {
  const token = localStorage.getItem('lk-tk') || getCookie('lk-tk');
  return !!token;
};

const sortTasksDesc = (list) =>
    [...list].sort((a, b) => {
      const aTime = new Date(a.updateTime || a.createTime || 0).getTime();
      const bTime = new Date(b.updateTime || b.createTime || 0).getTime();
      return bTime - aTime;
    });

// 进度显示动画用：每个任务一个“前端展示中的进度值”和一个计时器
const displayProgressMap = ref({});
const displayProgressTimers = new Map();

const clearProgressTimer = (taskId) => {
  const timer = displayProgressTimers.get(taskId);
  if (timer) {
    clearInterval(timer);
    displayProgressTimers.delete(taskId);
  }
};

const animateTaskProgress = (beforeTask, afterTask) => {
  const id = afterTask?.id;
  if (!id) return;

  const rawBefore = toNumber(beforeTask?.progress);
  const rawAfter = toNumber(afterTask?.progress);

  if (rawAfter === null || !Number.isFinite(rawAfter)) return;

  // 如果后端进度没有变大，就直接同步为最新值
  const currentDisplayed = toNumber(displayProgressMap.value[id]);
  let start = Number.isFinite(currentDisplayed)
      ? currentDisplayed
      : (Number.isFinite(rawBefore) ? rawBefore : 0);

  if (!Number.isFinite(start)) start = 0;

  if (rawAfter <= start) {
    clearProgressTimer(id);
    displayProgressMap.value = {
      ...displayProgressMap.value,
      [id]: rawAfter,
    };
    return;
  }

  // 从当前展示值缓慢 +1% / 秒 地逼近后端进度
  clearProgressTimer(id);
  displayProgressMap.value = {
    ...displayProgressMap.value,
    [id]: start,
  };

  const timer = window.setInterval(() => {
    const current = toNumber(displayProgressMap.value[id]);
    if (current === null || !Number.isFinite(current)) {
      clearProgressTimer(id);
      return;
    }
    if (current >= rawAfter) {
      clearProgressTimer(id);
      displayProgressMap.value = {
        ...displayProgressMap.value,
        [id]: rawAfter,
      };
      return;
    }
    const next = current + 1;
    displayProgressMap.value = {
      ...displayProgressMap.value,
      [id]: next,
    };
  }, 1000);

  displayProgressTimers.set(id, timer);
};

// 控制“100% 完成”瞬时展示的遮罩
const completionOverlays = ref({});

const triggerCompletionOverlay = (taskId, duration = 1000) => {
  if (!taskId) return;
  completionOverlays.value = {
    ...completionOverlays.value,
    [taskId]: true,
  };
  window.setTimeout(() => {
    const next = {...completionOverlays.value};
    delete next[taskId];
    completionOverlays.value = next;
  }, duration);
};

const updateTaskInList = (updatedTask) => {
  const nextList = tasks.value.map((item) => {
    if (item.id !== updatedTask.id) return item;
    const before = item;
    const after = {...item, ...updatedTask};
    // 当该任务首次拿到视频地址时，触发 100% 完成遮罩
    if (!before.videoUrl && after.videoUrl) {
      triggerCompletionOverlay(after.id);
    }
    // 更新时，驱动前端进度的缓慢动画
    animateTaskProgress(before, after);
    return after;
  });
  tasks.value = sortTasksDesc(nextList);
};

const shouldContinuePolling = (task) => {
  const state = normalizeState(task.state);
  if (state === '0' || state === '1' || state === '4') return true;
  // 当任务已成功但视频地址尚未生成时继续轮询，直到拿到视频地址。
  if (state === '2' && !task?.videoUrl) return true;
  return false;
};

const pollPendingTasks = async () => {
  const pending = tasks.value.filter((task) => shouldContinuePolling(task));
  if (!pending.length) return;
  try {
    const results = await Promise.allSettled(
        pending.map((task) => getCvTaskStatus(task.id))
    );
    results.forEach((res, index) => {
      if (res.status !== 'fulfilled') {
        console.error('poll task failed:', pending[index].id, res.reason);
        return;
      }
      const payload = res.value;
      if (payload?.code !== SUCCESS_CODE || !payload?.data) return;
      updateTaskInList(payload.data);
    });
  } catch (err) {
    console.error('poll pending tasks error:', err);
  }
};

const stopPolling = () => {
  if (pollTimer.value) {
    clearInterval(pollTimer.value);
    pollTimer.value = null;
  }
};

const startPolling = () => {
  stopPolling();
  pollPendingTasks();
  pollTimer.value = setInterval(pollPendingTasks, POLL_INTERVAL);
};

const MIN_SKELETON_MS = 1000;

const finishInitialLoading = (startedAt) => {
  const elapsed = Date.now() - startedAt;
  const delay = Math.max(0, MIN_SKELETON_MS - elapsed);
  window.setTimeout(() => {
    initialLoading.value = false;
    // Scroll after骨架屏消失，确保长列表也能滑到底部。
    nextTick(() => scrollToLatest('smooth'));
  }, delay);
};

const fetchTasks = async (page = 1, append = false) => {
  const startTime = !append ? Date.now() : 0;
  try {
    if (append) {
      loadingMore.value = true;
    } else {
      initialLoading.value = true;
    }
    const res = await listCvTasks(page, pageSize);

    if (res?.code !== SUCCESS_CODE || !res?.data) {
      Message.error(res?.msg || '获取任务列表失败');
      return;
    }

    const incoming = sortTasksDesc(res.data.list || []);
    tasks.value = append ? [...tasks.value, ...incoming] : incoming;
    currentPage.value = page;

    const totalPage = res.data?.totalPage || 1;
    hasMore.value = page < totalPage || res.data?.hasNext === true;

    startPolling();

    if (!append) {
      await nextTick();
      scrollToLatest('smooth');
    }
  } catch (error) {
    console.error('fetchTasks failed:', error);
    Message.error('列表获取失败，请稍后重试');
  } finally {
    if (append) {
      loadingMore.value = false;
    } else {
      finishInitialLoading(startTime);
    }
    loadingMore.value = false;
  }
};

const scrollToLatest = (behavior = 'smooth') => {
  const container = chatRef.value;
  if (!container) return;
  // Scroll after layout flush to ensure latest item is visible.
  requestAnimationFrame(() => {
    try {
      container.scrollTo({
        top: container.scrollHeight,
        behavior,
      });
    } catch (_) {
      // Fallback for older browsers.
      container.scrollTop = container.scrollHeight;
    }
  });
};

const handleScroll = () => {
  const container = chatRef.value;
  if (!container || !hasMore.value || loadingMore.value) return;
  if (container.scrollTop <= 60) {
    fetchTasks(currentPage.value + 1, true);
  }
};

const isFailed = (task) => normalizeState(task.state) === '3' || normalizeState(task.state) === '5';
const getFailedText = (task) => {
  if (normalizeState(task.state) === '5') {
    return "抠图失败，可尝试上传手工抠图."
  } else {
    return "可能网络出了点差错，可再次重新生成."
  }
}
const isCompleted = (task) => normalizeState(task.state) === '2';
const isGenerating = (task) => {
  const state = normalizeState(task.state);
  return state !== '2' && state !== '3';
};
const stateDisplay = (task) => {
  const state = normalizeState(task.state);
  if (state === '2') return '我已完成视频生成';
  if (state === '3') return '视频生成失败';
  return '正在生成视频';
};
const taskMainText = (task) => {
  if (task?.videoUrl) return '我已完成视频生成';
  const state = normalizeState(task.state);
  if (state === '3') return '视频生成失败';

  const displayVal = getDisplayProgressValue(task);
  const progressForStage = displayVal === 99.9 ? 99 : displayVal;
  const stage = findStageByProgress(progressForStage);
  if (stage) return stage.main;
  return stateDisplay(task);
};
const taskSubText = (task) => {
  if (task?.videoUrl) return '';
  const state = normalizeState(task.state);
  if (state === '3') return '可能网络或上传图片出了点差错,可再次重新生成';

  const displayVal = getDisplayProgressValue(task);
  const progressForStage = displayVal === 99.9 ? 99 : displayVal;
  const stage = findStageByProgress(progressForStage);
  return stage?.sub || '';
};

const getDisplayProgressValue = (task) => {
  const id = task?.id;
  const animatedRaw = id ? toNumber(displayProgressMap.value[id]) : null;
  const backendRaw = toNumber(task?.progress);
  const raw = animatedRaw ?? backendRaw;

  if (raw === null) return null;

  // 一旦有视频地址，直接视为 100%
  if (task?.videoUrl) {
    return 100;
  }

  const clamped = Math.max(0, Math.min(raw, 100));

  // 当进度超过 99% 且还没有视频地址时，统一展示为 99.9
  if (clamped > 99) {
    return 99.9;
  }

  // 其他情况只保留整数
  return Math.floor(clamped);
};

const displayProgressText = (task) => {
  const val = getDisplayProgressValue(task);
  if (val === null) return '0%';
  // 只有到达特殊档位时展示 99.9，其余都是整数
  if (val === 99.9) return '99.9%';
  return `${Math.round(val)}%`;
};

const formatDuration = (seconds) => {
  if (!seconds && seconds !== 0) return '00:00';
  const s = Math.max(0, Math.round(seconds));
  const m = Math.floor(s / 60)
      .toString()
      .padStart(2, '0');
  const r = (s % 60).toString().padStart(2, '0');
  return `${m}:${r}`;
};

const regenerateTask = async (task) => {
  if (!hasToken()) {
    showLoginModal && showLoginModal();
    return;
  }
  // 生成中不允许重新生成，避免频繁请求
  if (isGenerating(task)) {
    Message.error('视频正在生成中，请稍后再试');
    return;
  }
  if (!task?.sourceImage || actionLoading.value) return;
  try {
    actionLoading.value = task.id;
    const taskPayload = {
      imageUrl: task.sourceImage,
      matting: false,
      clarity: 'HD',
      orientation: 'portrait',
      duration: task.duration || 25,
      narration: '',
      attributes: {},
    };
    const taskRes = await createCvTask(taskPayload);
    if (taskRes?.code === 50029) {
      showQuotaModal.value = true;
      return;
    }
    if (taskRes?.code !== SUCCESS_CODE || taskRes?.success !== true) {
      Message.error(taskRes?.msg || '任务创建失败');
      return;
    }
    Message.success(taskRes?.msg || '任务创建成功');

    // 如果是失败任务，创建新任务后静默删除旧记录再刷新列表
    if (isFailed(task)) {
      try {
        await deleteCvTask(task.id);
      } catch (err) {
        console.error('silent delete failed task failed:', err);
      }
    }
    await fetchTasks(1, false);
  } catch (error) {
    console.error('regenerate failed:', error);
    Message.error('重新生成失败，请重试');
  } finally {
    actionLoading.value = '';
  }
};

const downloadVideo = (task) => {
  if (!task?.videoUrl) {
    Message.error('暂无可下载的视频');
    return;
  }
  const link = document.createElement('a');
  link.href = task.videoUrl + '?response-content-disposition=attachment';
  link.download = task.taskId ? `${task.taskId}.mp4` : 'video.mp4';
  link.target = "_blank";
  link.click();
};

const deleteTask = (task) => {
  pendingDeleteTask.value = task;
  showDeleteModal.value = true;
};

const closeDeleteModal = () => {
  if (deleteLoading.value) return;
  showDeleteModal.value = false;
  pendingDeleteTask.value = null;
};

const confirmDelete = async () => {
  if (!pendingDeleteTask.value || deleteLoading.value) return;
  if (!hasToken()) {
    showLoginModal && showLoginModal();
    return;
  }
  try {
    deleteLoading.value = true;
    const res = await deleteCvTask(pendingDeleteTask.value.id);
    if (res?.code === SUCCESS_CODE) {
      await fetchTasks(1, false);
      Message.success(res?.msg || '删除成功');
    } else if (res?.code === 50011) {
      Message.error(res?.msg || '无权限操作');
    } else {
      Message.error(res?.msg || '删除失败，请稍后重试');
    }
  } catch (error) {
    console.error('delete task failed:', error);
    Message.error('删除失败，请重试');
  } finally {
    deleteLoading.value = false;
    closeDeleteModal();
  }
};

const setVideoRef = (id, el) => {
  if (el) {
    videoRefs.set(id, el);
  } else {
    videoRefs.delete(id);
  }
};

const handlePlay = (id) => {
  playingId.value = id;
};

const handlePause = (id) => {
  if (playingId.value === id) {
    playingId.value = '';
  }
};

const isPlaying = (id) => playingId.value === id;

const toggleVideo = (id) => {
  const video = videoRefs.get(id);
  if (!video) return;
  if (isPlaying(id)) {
    video.pause();
  } else {
    // pause others
    videoRefs.forEach((refVideo, key) => {
      if (key !== id && !refVideo.paused) {
        refVideo.pause();
      }
    });
    video.play();
  }
};

const handleTimeUpdate = (id, evt) => {
  const video = evt?.target;
  if (!video || Number.isNaN(video.duration) || video.duration === Infinity) return;
  const remain = Math.max(0, video.duration - video.currentTime);
  remainingTimes.value = {...remainingTimes.value, [id]: remain};
};

const displayDuration = (task) => {
  const id = task.id;
  if (isPlaying(id) && remainingTimes.value[id] !== undefined) {
    return formatDuration(remainingTimes.value[id]);
  }
  return task.duration ? formatDuration(task.duration) : '00:00';
};

// ---------- 全屏控制 ----------
const requestVideoFullscreen = (videoEl) => {
  if (!videoEl) return;
  const el = videoEl;
  if (el.requestFullscreen) {
    el.requestFullscreen();
  } else if (el.webkitRequestFullscreen) {
    el.webkitRequestFullscreen();
  } else if (el.webkitEnterFullscreen) {
    // iOS Safari 专有的视频全屏
    el.webkitEnterFullscreen();
  }
};

const exitFullscreen = () => {
  const doc = document;
  if (doc.exitFullscreen) {
    doc.exitFullscreen();
  } else if (doc.webkitExitFullscreen) {
    doc.webkitExitFullscreen();
  }
};

const toggleFullscreen = (id) => {
  const video = videoRefs.get(id);
  if (!video) return;

  const doc = document;
  const isCurrentFullscreen =
      doc.fullscreenElement === video ||
      doc.webkitFullscreenElement === video;

  if (isCurrentFullscreen) {
    exitFullscreen();
    fullscreenVideoId.value = '';
    return;
  }

  // 如已有其它元素全屏，先退出再请求当前视频全屏
  if (doc.fullscreenElement || doc.webkitFullscreenElement) {
    exitFullscreen();
    setTimeout(() => requestVideoFullscreen(video), 50);
  } else {
    requestVideoFullscreen(video);
  }
  fullscreenVideoId.value = id;
};

const handleFullscreenChange = () => {
  const doc = document;
  const anyFullscreenEl = doc.fullscreenElement || doc.webkitFullscreenElement;
  if (!anyFullscreenEl) {
    // 退出全屏（包括按 Esc）时重置状态
    fullscreenVideoId.value = '';
  }
};

const handleUploadClick = () => {
  if (!hasToken()) {
    showLoginModal && showLoginModal();
    return;
  }
  if (uploading.value) return;
  fileInput.value?.click();
};

const validateFile = (file) => {
  if (!file) return false;
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'video/mp4'];
  const validExtensions = ['.jpg', '.jpeg', '.png', '.mp4'];
  const fileName = file.name.toLowerCase();
  const fileExtension = fileName.substring(fileName.lastIndexOf('.'));

  if (!validTypes.includes(file.type) && !validExtensions.includes(fileExtension)) {
    Message.error('不支持的文件格式，请上传 JPG、PNG 或 MP4 格式的文件');
    return false;
  }

  const maxSize = 4 * 1024 * 1024; // 20MB
  if (file.size > maxSize) {
    Message.error('文件大小不能超过 20MB');
    return false;
  }

  return true;
};

const processFile = async (file) => {
  if (!file || uploading.value) return;
  if (!hasToken()) {
    showLoginModal && showLoginModal();
    return;
  }
  if (!validateFile(file)) return;

  try {
    uploading.value = true;
    const formData = new FormData();
    formData.append('image', file);

    const uploadRes = await uploadClothesImage(formData);

    if (uploadRes?.code !== SUCCESS_CODE || !uploadRes?.data) {
      Message.error(uploadRes?.msg || '图片上传失败');
      return;
    }

    const data = uploadRes.data;
    const taskPayload = {
      imageUrl: data.url,
      matting: data.matting,
      target: data.target,
      clarity: 'HD',
      orientation: 'portrait',
      duration: 10,
      narration: '',
      attributes: {},
    };

    const taskRes = await createCvTask(taskPayload);

    if (taskRes?.code === 50029) {
      showQuotaModal.value = true;
      return;
    }
    if (taskRes?.code !== SUCCESS_CODE || taskRes?.success !== true) {
      Message.error(taskRes?.msg || '任务创建失败');
      return;
    }

    Message.success(taskRes?.msg || '任务创建成功');
    fetchTasks(1, false);
    // 顶部的算力 UI 上减 1，不需要从后台拉取算力配额
    emitQuotaDelta(-1);
  } catch (error) {
    console.error('upload flow failed:', error);
    Message.error('上传或创建任务失败，请重试');
  } finally {
    uploading.value = false;
  }
};

const handleFileChange = async (event) => {
  const file = event.target.files?.[0];
  await processFile(file);
  if (event?.target) {
    event.target.value = '';
  }
};

const handleDragOver = (event) => {
  event.preventDefault();
  if (!uploading.value) {
    isDragOver.value = true;
  }
};

const handleDragLeave = (event) => {
  event.preventDefault();
  // 只有当离开整个拖拽区域时才取消高亮
  if (!event.currentTarget.contains(event.relatedTarget)) {
    isDragOver.value = false;
  }
};

const handleDrop = async (event) => {
  event.preventDefault();
  isDragOver.value = false;

  const files = event.dataTransfer?.files;
  if (!files || files.length === 0) return;

  const file = files[0];
  await processFile(file);
};

onMounted(() => {
  fetchTasks();
  document.addEventListener('fullscreenchange', handleFullscreenChange);
  document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
});

onUnmounted(() => {
  stopPolling();
  document.removeEventListener('fullscreenchange', handleFullscreenChange);
  document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
});
</script>

<style scoped>
.chat-page {
  height: calc(100vh - 65px);
  background: #0e0a05;
  color: #e7e4df;
  display: flex;
  justify-content: center;
  overflow-y: auto;
  overflow-x: hidden;
}

.page-surface {
  position: relative;
  width: 100%;
  min-width: 1440px;
  padding: 24px;
  box-sizing: border-box;
  display: flex;
  gap: 32px;
}

.list-shell {
  position: relative;
  width: 100%;
  border: none;
  background: transparent;
  padding: 0;
  box-sizing: border-box;
}

.task-list {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.column-reverse {
  flex-direction: column-reverse;
}

.task-card {
  padding: 24px 16px;
  background: transparent;
  display: flex;
  flex-direction: column;
  gap: 16px;
  border-bottom: 1px solid #4a4136;
}

.task-list:not(.column-reverse) .task-card:last-child {
  border-bottom: none;
}

.task-list.column-reverse .task-card:first-child {
  border-bottom: none;
}

.item-header {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #ffffff;
  font-size: 18px;
  font-weight: 600;
}

.state-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #d0ccc1;
  box-shadow: 0 0 0 4px rgba(250, 216, 124, 0.15);
}

.dot-success {
  background: #f050c1;
}

.dot-pending {
  background: #f7cf73;
}

.dot-failed {
  background: #f86d6d;
}

.state-sub-text {
  color: #D0CCC1;
  font-family: "PingFang SC";
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 24px */
}

.state-text {
  color: #ffffff;
}

.meta {
  margin-left: auto;
  color: #9a9285;
  font-size: 13px;
}

.item-body {
  display: grid;
  grid-template-columns: 140px auto;
  gap: 24px;
  align-items: start;
  position: relative;
}

.item-left {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.source-thumb {
  width: 124px;
  height: 124px;
  border-radius: 24px;
  padding: 8px;
  background: #4a4136;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
}

.source-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 16px;
}

.item-preview {
  position: relative;
  width: 400px;
  height: 533px;
  border-radius: 0 24px 24px 24px;
  background: #eceff3;
  display: flex;
  align-items: center;
  justify-content: center;
}

.preview-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: #000;
}

.preview-video.fullscreen-video {
  object-fit: contain;
}

.play-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.08), rgba(0, 0, 0, 0.25));
  border: none;
  cursor: pointer;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;
}

.play-overlay.visible {
  opacity: 1;
  pointer-events: auto;
}

.completion-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3;
}

.item-preview:hover .play-overlay {
  opacity: 1;
  pointer-events: auto;
}

.play-icon {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.45);
  position: relative;
}

.play-toggle-icon {
  width: 64px;
  height: 64px;
}

.duration-chip {
  position: absolute;
  right: 12px;
  bottom: 12px;
  background: rgba(0, 0, 0, 0.5);
  color: #fff;
  padding: 4px 12px;
  border-radius: 10px;
  font-size: 14px;
}

.action-stack {
  position: absolute;
  right: -136px;
  bottom: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.ghost-btn {
  width: 110px;
  height: 40px;
  border-radius: 8px;
  border: 1px solid #fad87c;
  background: rgba(71, 71, 71, 0.4);
  color: #d0ccc1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.15s ease, transform 0.1s ease;
}

.ghost-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.ghost-btn:hover:not(:disabled) {
  opacity: 0.9;
}

.ghost-btn:active:not(:disabled) {
  transform: translateY(1px);
}

.ghost-btn.small {
  width: 110px;
}

.ghost-btn.filled {
  background: #47474766;
  border-color: #fad87c;
  color: #d0ccc1;
}

.ghost-icon {
  width: 16px;
  height: 16px;
  display: inline-block;
  background-size: contain;
  background-repeat: no-repeat;
}

.ghost-icon.refresh {
  background-image: url('@/assets/refresh_icon.png');
}

.ghost-icon.delete {
  background-image: url('@/assets/delete_icon.png');
}

.ghost-icon.download {
  background-image: url('@/assets/download_icon.png');
}

.item-preview.loading {
  background: #eceff3;
}

.loading-card {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 16px;
  color: #d6d0c4;
  text-align: center;
  background: url('@/assets/cv_item_loading_bg.png') center/cover no-repeat;
  filter: blur(0);
}

.failed-thumb {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 0 24px 24px 24px;
}

.failed-thumb img {
  width: 90px;
  height: 90px;
  object-fit: cover;
}

.failed-text {
  margin-top: 12px;
  color: white;
  font-size: 18px;
  font-weight: 600;
}
.failed-tips-text{
  font-family: "PingFang SC";
  font-weight: 400;
  font-size: 16px;
  line-height: 150%;
}

.loading-ring-wrapper {
  position: relative;
  width: 160px;
  height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.loading-ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: conic-gradient(#FBBB62 20deg, #FFBE6433 160deg, rgba(255, 255, 255, 0.14) 200deg, rgba(255, 255, 255, 0) 360deg);
  mask: radial-gradient(farthest-side, transparent calc(50% + 25px), black 0);
  -webkit-mask: radial-gradient(farthest-side, transparent calc(50% + 25px), black 0);
  animation: spin 1.8s linear infinite;
  box-shadow: 0 12px 42px rgba(0, 0, 0, 0.22);
}

.loading-ring-center {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #f8e0a2;
  font-size: 20px;
  font-weight: 600;
}

.loading-ring-percent {
  letter-spacing: 0.02em;
  color: #FBBB62;
  font-family: "PingFang SC";
  font-size: 36px;
  font-style: normal;
  font-weight: 600;
  line-height: 150%; /* 54px */
}

.loading-stage-text {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  text-align: center;
  padding: 0 24px;
}

.loading-stage-main {
  color: #F5E6C7;
  font-family: "PingFang SC";
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 150%;
}

.loading-stage-sub {
  color: #D0CCC1;
  font-family: "PingFang SC";
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
}

.loading-text {
  color: #B9B1A4;
  font-family: "PingFang SC";
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 21px */
  position: absolute;
  bottom: 30px;
}

.action-stack.loading-actions {
  right: -136px;
  bottom: 12px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.empty-state {
  height: 100%;
  min-height: 420px;
  border: 1px dashed rgba(221, 180, 99, 0.3);
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #b5ab9a;
}

.empty-title {
  font-size: 20px;
  color: #ffffff;
  font-weight: 600;
}

.empty-desc {
  font-size: 15px;
}

.load-tip {
  text-align: center;
  margin-top: 12px;
  color: #e7e4df;
}

.load-tip.muted {
  color: #7a7266;
}

.delete-modal-mask {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
}

.delete-modal {
  width: 340px;
  max-width: calc(100% - 48px);
  border-radius: 20px;
  background: linear-gradient(180deg, #2b241d 0%, #1a1510 100%);
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
  padding: 32px 28px;
  box-sizing: border-box;
  text-align: center;
  color: #e7e4df;
}

.delete-icon-wrap {
  width: 64px;
  height: 64px;
  margin: 0 auto 16px;
  border-radius: 16px;
  background: rgba(250, 216, 124, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
}

.delete-icon {
  width: 28px;
  height: 28px;
  background-image: url('https://www.figma.com/api/mcp/asset/d0d38dc5-2809-4985-9e6c-1993da53cdec');
  background-size: contain;
  background-repeat: no-repeat;
}

.delete-title {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 8px;
}

.delete-desc {
  font-size: 14px;
  color: #b5ab9a;
  margin-bottom: 24px;
}

.delete-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.modal-btn {
  min-width: 140px;
  height: 44px;
  border-radius: 12px;
  border: 1px solid #fad87c;
  background: transparent;
  color: #e7e4df;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s ease, transform 0.1s ease;
}

.modal-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.modal-btn:hover:not(:disabled) {
  opacity: 0.9;
}

.modal-btn:active:not(:disabled) {
  transform: translateY(1px);
}

.modal-btn.danger {
  background: linear-gradient(
      111deg,
      rgba(188, 130, 58, 1) 2%,
      rgba(247, 207, 115, 1) 40%,
      rgba(227, 156, 24, 1) 75%
  );
  color: #120f0d;
  border-color: #ddb463;
}

.modal-btn.ghost {
  background: rgba(74, 65, 54, 0.6);
}

.floating-upload {
  position: fixed;
  right: 48px;
  bottom: 48px;
  width: 492px;
  max-width: calc(100% - 32px);
  z-index: 50;
  transition: transform 0.2s ease;
}

.floating-upload.drag-over {
  transform: scale(1.02);
}

.floating-upload.drag-over .upload-card {
  border-color: rgba(250, 216, 124, 0.4);
  background: rgba(51, 48, 44, 0.95);
}

.upload-card {
  width: 100%;
  border-radius: 16px;
  backdrop-filter: blur(2px);
  background: rgba(51, 48, 44, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.06);
  padding: 42px 24px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  box-shadow: 0 18px 36px rgba(0, 0, 0, 0.36);
  transition: border-color 0.2s ease, background 0.2s ease;
}

.upload-cta {
  height: 50px;
  min-width: 192px;
  padding: 0 16px;
  border-radius: 8px;
  border: 1px solid #ddb463;
  background: linear-gradient(
      111deg,
      rgba(188, 130, 58, 1) 2%,
      rgba(247, 207, 115, 1) 40%,
      rgba(227, 156, 24, 1) 75%
  );
  color: #120f0d;
  font-size: 16px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  transition: transform 0.1s ease, box-shadow 0.2s ease;
  position: relative;
  overflow: hidden;
}

.upload-cta:disabled {
  cursor: not-allowed;
  opacity: 0.9;
  box-shadow: none;
}

.upload-cta.loading {
}

.upload-cta.loading span {
  position: relative;
  z-index: 2;
}

.upload-cta.loading .cta-icon {
  position: relative;
  z-index: 2;
}

.cta-flash {
  position: absolute;
  inset: 0;
  background: linear-gradient(
      120deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.32) 40%,
      rgba(255, 255, 255, 0) 80%
  );
  transform: translateX(-120%);
  animation: flash-run 1.2s ease-in-out infinite;
  z-index: 1;
}

.upload-cta:hover {
  box-shadow: 0 16px 38px rgba(0, 0, 0, 0.25);
}

.upload-cta:active {
  transform: translateY(1px);
}

.cta-icon {
  width: 24px;
  height: 24px;
}

.upload-copy {
  text-align: center;
  color: #e7e4df;
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 16px;
  line-height: 1.5;
  font-weight: 600;
}

.copy-sub {
  color: #807666;
  font-size: 16px;
  font-weight: 400;
}

.skeleton-group {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.skeleton-card {
  background: rgba(26, 22, 17, 0.55);
  border-radius: 18px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  padding: 24px 16px;
}

.skeleton-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.12);
}

.skeleton-line,
.skeleton-rect,
.skeleton-thumb,
.skeleton-button,
.skeleton-action {
  position: relative;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.08);
}

.skeleton-line::after,
.skeleton-rect::after,
.skeleton-thumb::after,
.skeleton-button::after,
.skeleton-action::after {
  content: '';
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.18), transparent);
  animation: shimmer 0.5s ease-in-out infinite;
}

.skeleton-line {
  height: 16px;
  border-radius: 8px;
}

.skeleton-line.wide {
  width: 60%;
}

.header-line {
  width: 180px;
}

.skeleton-body {
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 24px;
  margin-top: 16px;
}

.skeleton-left {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.skeleton-thumb {
  width: 124px;
  height: 124px;
  border-radius: 24px;
}

.skeleton-button {
  width: 110px;
  height: 40px;
  border-radius: 8px;
}

.skeleton-preview {
  position: relative;
  width: 100%;
  max-width: 400px;
  aspect-ratio: 3 / 4;
  border-radius: 0 24px 24px 24px;
}

.skeleton-rect {
  width: 100%;
  height: 100%;
  border-radius: inherit;
}

.skeleton-actions {
  position: absolute;
  right: -136px;
  bottom: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.skeleton-action {
  width: 110px;
  height: 40px;
  border-radius: 8px;
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  60% {
    transform: translateX(100%);
  }
  100% {
    transform: translateX(100%);
  }
}

@media (max-width: 1280px) {
  .page-surface {
    padding: 32px 24px 80px;
    flex-direction: column;
  }

  .item-body {
    grid-template-columns: 1fr;
  }

  .action-stack,
  .action-stack.loading-actions {
    position: static;
    flex-direction: row;
    justify-content: flex-end;
    width: 100%;
    margin-top: 12px;
    gap: 8px;
  }

  .floating-upload {
    position: relative;
    right: auto;
    bottom: auto;
    width: 100%;
    max-width: 100%;
    margin-top: 16px;
  }

  .skeleton-body {
    grid-template-columns: 1fr;
  }

  .skeleton-preview {
    max-width: 100%;
  }

  .skeleton-actions {
    position: static;
    flex-direction: row;
    justify-content: flex-end;
    width: 100%;
    margin-top: 12px;
  }
}
</style>
